export const GUIDED_RESUME_RESULT_STATUS = {
  COMPLETE: 'complete_resume',
  PARTIAL: 'partial_resume_draft',
  INSUFFICIENT: 'insufficient_information',
  FAILED: 'generation_failure',
  LEGACY_UNKNOWN: 'legacy_unverified_result',
  IN_PROGRESS: 'in_progress',
};

function readResultStatus(session) {
  return (
    session?.final_result_status
    || session?.result_status
    || session?.final_resume_data?.final_result_status
    || session?.final_resume_data?.result_status
    || ''
  );
}

export function getGuidedResumeResultView(session) {
  const rawStatus = readResultStatus(session);
  const hasFinalText = Boolean((session?.final_resume_text || '').trim());
  const isCompleted = session?.status === 'completed';

  if (!isCompleted) {
    return {
      status: GUIDED_RESUME_RESULT_STATUS.IN_PROGRESS,
      resultStatus: rawStatus || null,
      heading: '',
      description: '',
      scoreLabel: 'Completeness Score',
      previewHeading: 'Generated Resume Preview',
      noticeTitle: '',
      noticeMessage: '',
      noticeTone: 'default',
      toastMessage: '',
      canCopy: false,
      canExport: false,
      showPreview: false,
      isComplete: false,
      isPartial: false,
      isInsufficient: false,
      isFailure: false,
      isLegacyUnknown: false,
    };
  }

  if (rawStatus === GUIDED_RESUME_RESULT_STATUS.COMPLETE) {
    return {
      status: GUIDED_RESUME_RESULT_STATUS.COMPLETE,
      resultStatus: rawStatus,
      heading: 'Resume Ready',
      description: 'Your general ATS-friendly resume has been generated. You can copy it, export it, or start a fresh guided session.',
      scoreLabel: 'Completeness Score',
      previewHeading: 'Generated Resume Preview',
      noticeTitle: '',
      noticeMessage: '',
      noticeTone: 'success',
      toastMessage: 'Your guided resume is ready.',
      canCopy: hasFinalText,
      canExport: hasFinalText,
      showPreview: hasFinalText,
      isComplete: true,
      isPartial: false,
      isInsufficient: false,
      isFailure: false,
      isLegacyUnknown: false,
    };
  }

  if (rawStatus === GUIDED_RESUME_RESULT_STATUS.PARTIAL) {
    return {
      status: GUIDED_RESUME_RESULT_STATUS.PARTIAL,
      resultStatus: rawStatus,
      heading: 'Partial Skills Draft Ready',
      description: 'This is a partial professional skills draft, not a complete chronological resume. It preserves verified skills while identifying the missing chronology needed for a full resume.',
      scoreLabel: 'Draft Score',
      previewHeading: 'Partial Professional Skills Draft Preview',
      noticeTitle: 'Partial draft',
      noticeMessage: 'Chronological employers, role titles, dates, education, and accomplishment details are still needed before this can be treated as a complete resume.',
      noticeTone: 'warning',
      toastMessage: 'Partial skills draft is ready for review.',
      canCopy: hasFinalText,
      canExport: hasFinalText,
      showPreview: hasFinalText,
      isComplete: false,
      isPartial: true,
      isInsufficient: false,
      isFailure: false,
      isLegacyUnknown: false,
    };
  }

  if (rawStatus === GUIDED_RESUME_RESULT_STATUS.INSUFFICIENT) {
    return {
      status: GUIDED_RESUME_RESULT_STATUS.INSUFFICIENT,
      resultStatus: rawStatus,
      heading: 'More Information Needed',
      description: 'There was not enough verified information to create a resume or exportable draft. Add real role, employer, date, education, and accomplishment details before trying again.',
      scoreLabel: 'Information Score',
      previewHeading: 'Information Needed',
      noticeTitle: 'Incomplete information',
      noticeMessage: 'Copy and DOCX/PDF export are unavailable because this result does not contain a usable resume draft.',
      noticeTone: 'warning',
      toastMessage: 'More verified information is needed before a resume can be generated.',
      canCopy: false,
      canExport: false,
      showPreview: hasFinalText,
      isComplete: false,
      isPartial: false,
      isInsufficient: true,
      isFailure: false,
      isLegacyUnknown: false,
    };
  }

  if (rawStatus === GUIDED_RESUME_RESULT_STATUS.FAILED) {
    return {
      status: GUIDED_RESUME_RESULT_STATUS.FAILED,
      resultStatus: rawStatus,
      heading: 'Generation Did Not Complete',
      description: 'The guided resume generation did not complete successfully. No completed resume is available from this result.',
      scoreLabel: 'Completeness Score',
      previewHeading: 'Generation Status',
      noticeTitle: 'Generation failed',
      noticeMessage: 'Copy and export are unavailable for this failed generation.',
      noticeTone: 'warning',
      toastMessage: 'Guided resume generation did not complete.',
      canCopy: false,
      canExport: false,
      showPreview: false,
      isComplete: false,
      isPartial: false,
      isInsufficient: false,
      isFailure: true,
      isLegacyUnknown: false,
    };
  }

  return {
    status: GUIDED_RESUME_RESULT_STATUS.LEGACY_UNKNOWN,
    resultStatus: rawStatus || null,
    heading: 'Generated Draft Needs Review',
    description: 'This older guided result does not include the newer validation status. Review it carefully before using it; DOCX/PDF export is unavailable for this legacy result.',
    scoreLabel: 'Draft Score',
    previewHeading: 'Generated Draft Preview',
    noticeTitle: 'Validation status unavailable',
    noticeMessage: 'This result was created before guided validation status was stored, so it is not being treated as a verified complete resume.',
    noticeTone: 'warning',
    toastMessage: 'Generated draft loaded for review.',
    canCopy: hasFinalText,
    canExport: false,
    showPreview: hasFinalText,
    isComplete: false,
    isPartial: false,
    isInsufficient: false,
    isFailure: false,
    isLegacyUnknown: true,
  };
}

export function buildGuidedResumeExportPayload(session) {
  const guidedResumeData = session?.final_resume_data;

  if (!guidedResumeData || typeof guidedResumeData !== 'object') {
    return null;
  }

  return {
    ...guidedResumeData,
    final_result_status: session?.final_result_status
      || guidedResumeData.final_result_status
      || guidedResumeData.result_status
      || null,
    source_type: session?.source_type || guidedResumeData.source_type || null,
    source_validation_summary: session?.source_validation_summary
      || guidedResumeData.source_validation_summary
      || null,
  };
}
